import {
  AttendeeId,
  MeetingTypeRef,
  ReserveMeetingAttemptRequest,
  ReserveTimeSlotRequest,
  UserAttendeeId,
  MeetingAttendees,
  AvailabilityChecks,
  MeetingAttemptResponse,
} from '@chilipiper/api-client/src/user-availability'
import {
  GroupSchedulingLink,
  OptionMeetingTypeOverride,
} from '@chilipiper/api-client/src/team-scheduling'
import { MeetingLocations } from '@chilipiper/api-client/src/meeting-types'
import { SelectRouteResponse } from '@chilipiper/api-client/src/handoff'
import { MeetingHandling } from '@chilipiper/api-client/src/distribution'
import { MeetingLimitSource } from '../meeting-host-limit'
import { AvailabilityOptions, BuildBaseRequest, BaseRequest } from './types'

export const isInternalUser = (attendeeId: AttendeeId): attendeeId is UserAttendeeId =>
  attendeeId.type === 'UserAttendeeId'

export const buildRequestBase = ({
  intervalDuration,
  intervalStartsAt,
  meetingTypeId,
  meetingTypeVersion = new Date().toISOString(),
  afterBufferDurationOverride,
  beforeBufferDurationOverride,
  meetingDurationOverride,
}: BuildBaseRequest): BaseRequest => {
  return {
    interval: {
      duration: intervalDuration,
      startsAt: intervalStartsAt,
    },
    meetingTypeRef: {
      id: meetingTypeId,
      timestamp: meetingTypeVersion,
    },
    meetingTypeOverride: {
      meetingDurationOverride: meetingDurationOverride,
      beforeBufferDurationOverride: beforeBufferDurationOverride,
      afterBufferDurationOverride: afterBufferDurationOverride,
    },
  }
}

export const getUserIdsFromAvailabilityOptions = (
  availabilityOptions: AvailabilityOptions,
  additionalRequiredAttendees: string[] = []
) => {
  if ('assigneeIds' in availabilityOptions) {
    return availabilityOptions.assigneeIds.concat(additionalRequiredAttendees)
  }

  if ('rankedAssigneeIds' in availabilityOptions) {
    return availabilityOptions.rankedAssigneeIds.concat(additionalRequiredAttendees)
  }

  return availabilityOptions.userIds.concat(additionalRequiredAttendees)
}

export type MeetingTypeOverride = OptionMeetingTypeOverride & {
  duration?: string
  locations?: MeetingLocations
}

type ReserveTimeSlotProps = {
  userIds: string[]
  slotStartsAt: string
  meetingTypeOverride?: Partial<MeetingTypeOverride>
  meetingTypeRef: MeetingTypeRef
  meetingLimit?: MeetingLimitSource
  availabilityChecks?: AvailabilityChecks
}

export const buildReserveTimeSlotPayload = ({
  userIds,
  slotStartsAt,
  meetingTypeOverride,
  meetingLimit,
  meetingTypeRef,
  availabilityChecks,
}: ReserveTimeSlotProps): ReserveTimeSlotRequest | ReserveMeetingAttemptRequest => {
  const meetingTypeOverrideProps = getReservationMeetingTypeOverride(meetingTypeOverride)

  return {
    meetingTypeRef,
    slotStartsAt,
    userIds,
    durationOverride: meetingTypeOverrideProps.durationOverride,
    afterBufferDurationOverride: meetingTypeOverrideProps.afterBufferDurationOverride,
    beforeBufferDurationOverride: meetingTypeOverrideProps.beforeBufferDurationOverride,
    meetingLimit: meetingLimit ?? {
      type: 'NoMeetingLimit',
    },
    availabilityChecks: availabilityChecks ?? {
      type: 'All',
    },
  }
}

export const getReservationMeetingTypeOverride = (
  meetingTypeOverride?: Partial<MeetingTypeOverride>
) => {
  return {
    durationOverride: meetingTypeOverride?.duration,
    afterBufferDurationOverride:
      meetingTypeOverride?.buffers?.after.type === 'Change'
        ? meetingTypeOverride?.buffers.after.value
        : undefined,
    beforeBufferDurationOverride:
      meetingTypeOverride?.buffers?.before.type === 'Change'
        ? meetingTypeOverride?.buffers.before.value
        : undefined,
  }
}

export const getAdditionalRequiredAttendeesId = (attendees?: MeetingAttendees) => {
  if (!attendees || attendees.type === 'NoAdditionalAttendees') {
    return []
  }

  return attendees.attendees
    .filter(attendee => isInternalUser(attendee.id) && !attendee.optional)
    .map(attendee => (isInternalUser(attendee.id) ? attendee.id.userId : ''))
}

const getUniqUserIdsList = (userIds: string[]) => Array.from(new Set(userIds))

type MeetingAttemptReservationUserIdsAndMeetingLimit = {
  meetingLimit: MeetingLimitSource
  userIds: string[]
}

export const getMeetingAttemptReservationUserIdsAndMeetingLimit = ({
  meetingAttempt,
  routing,
  availableAssignees,
  algorithm,
  groupSchedulingLink,
  meetingLimit = {
    type: 'NoMeetingLimit',
  },
  distributionId,
}: {
  meetingAttempt: MeetingAttemptResponse
  routing?: SelectRouteResponse
  distributionId?: string
  algorithm?: MeetingHandling['type']
  availableAssignees?: string[]
  groupSchedulingLink?: GroupSchedulingLink
  meetingLimit?: MeetingLimitSource
}): MeetingAttemptReservationUserIdsAndMeetingLimit | undefined => {
  if (meetingAttempt && meetingAttempt?.type !== 'NotFound') {
    const additionalAttendees = getAdditionalRequiredAttendeesId(
      meetingAttempt.attempt.additionalAttendees
    )

    if (
      meetingAttempt.attempt.productFeature.type === 'AdminIndividualSchedulingLink' ||
      meetingAttempt.attempt.productFeature.type === 'PersonalIndividualSchedulingLink'
    ) {
      return {
        meetingLimit,
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.hostId,
          ...additionalAttendees,
        ]),
      }
    }

    if (meetingAttempt.attempt.productFeature.type === 'HandoffRouting') {
      let userIds: string[] = []

      if (routing?.selectedAssignee.type === 'User') {
        userIds = getUniqUserIdsList([routing.selectedAssignee.id, ...additionalAttendees])
      }

      if (routing?.selectedAssignee.type === 'UserFromDistribution') {
        userIds = getUniqUserIdsList([routing.selectedAssignee.user, ...additionalAttendees])
      }

      if (
        routing?.selectedAssignee.type === 'FromDistribution' &&
        routing?.selectedAssignee.algorithm === 'Strict'
      ) {
        userIds = getUniqUserIdsList([routing.selectedAssignee.users[0], ...additionalAttendees])
      }

      return {
        meetingLimit,
        userIds,
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink' &&
      meetingAttempt.attempt.productFeature.assigneeId &&
      distributionId
    ) {
      return {
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.assigneeId,
          ...additionalAttendees,
        ]),
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink' &&
      algorithm === 'Strict' &&
      availableAssignees?.length &&
      distributionId
    ) {
      return {
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([availableAssignees[0], ...additionalAttendees]),
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'OwnershipSchedulingLink' &&
      meetingAttempt.attempt.productFeature.assignment?.assigneeId &&
      distributionId
    ) {
      return {
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.assignment.assigneeId,
          ...additionalAttendees,
        ]),
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'GroupSchedulingLink' &&
      groupSchedulingLink
    ) {
      const hostId = meetingAttempt.attempt.productFeature.hostId ?? groupSchedulingLink.host.userId

      return {
        meetingLimit,
        userIds: getUniqUserIdsList([hostId, ...additionalAttendees]),
      }
    }
  }
}
